import React, { createContext, useContext, ReactNode } from 'react';
import { UserRole, UserRoleValue } from '@/services/urlService';

interface RoleContextValue {
  role: UserRoleValue;
  clientId?: string;
  token?: string;
}

const RoleContext = createContext<RoleContextValue | undefined>(undefined);

interface RoleProviderProps {
  role: UserRoleValue;
  clientId?: string;
  token?: string;
  children: ReactNode;
}

/**
 * Role-based context provider that provides user role and authentication context
 * to all child components. This eliminates the need to pass role, clientId, and token
 * through props at every level.
 */
export const RoleProvider: React.FC<RoleProviderProps> = ({
  role,
  clientId,
  token,
  children,
}) => {
  const value = {
    role,
    clientId,
    token,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

/**
 * Hook to access the role context
 * @throws Error if used outside of RoleProvider
 */
export const useRole = (): RoleContextValue => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

/**
 * Hook to check if the current user has a specific role
 */
export const useHasRole = (requiredRole: UserRoleValue): boolean => {
  const { role } = useRole();
  return role === requiredRole;
};

/**
 * Hook to check if the current user is an admin
 */
export const useIsAdmin = (): boolean => {
  return useHasRole(UserRole.ADMIN);
};

/**
 * Hook to check if the current user is an editor
 */
export const useIsEditor = (): boolean => {
  return useHasRole(UserRole.EDITOR);
};

/**
 * Hook to check if the current user is a client
 */
export const useIsClient = (): boolean => {
  return useHasRole(UserRole.CLIENT);
};
